import { mat4, ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import { Grid } from '~/engine/data/common/Grid';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { View } from '~/engine/view/View';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';
import {
    buildSnappedGrid,
    clampTranslationToImageBounds,
    flattenSlices,
    unflattenToSlices,
} from './transformUtils';

/** Per-sibling state saved at drag start. */
interface SiblingState {
    roi: RegionOfInterest;
    originalGrid: Grid;
    originalFlatPixels: Uint8Array;
    textureWrite?: TextureWrite;
}

/**
 * Action that handles interactive translation (dragging) of a structure's
 * SDF texture by shifting the texture grid origin.
 *
 * During drag only the grid origin is updated for smooth visual feedback.
 * On release the SDF pixels are resampled onto a grid snapped to the
 * image voxel lattice so the contours match what the user saw during
 * the drag without sub-voxel misalignment.
 *
 * When {@link _siblings} is non-empty, the same translation delta is
 * applied to each sibling support structure.
 */
export class StructureTranslateAction extends ToolAction {
    private readonly _roi: RegionOfInterest;
    private readonly _siblings: readonly RegionOfInterest[];

    /** The view in which the drag started — all coordinate
     *  conversions must use this view to avoid cross-view
     *  projection mismatches. */
    private _startView?: PlaneView;
    private _startPatient?: ReadonlyVec3;
    private _originalGrid?: Grid;
    private _originalFlatPixels?: Uint8Array;
    private _textureWrite?: TextureWrite;
    private _siblingStates: SiblingState[] = [];

    constructor(roi: RegionOfInterest, siblings?: readonly RegionOfInterest[]) {
        super();
        this._roi = roi;
        this._siblings = siblings ?? [];
    }

    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._startView = view;
        this._startPatient = view.clientToPatientCoordinates(coordinates);
        this._originalGrid = texture.textureGrid.clone();
        this._originalFlatPixels = flattenSlices(
            texture.pixels,
            this._originalGrid
        );

        // Snapshot sibling grids and pixels
        this._siblingStates = [];
        for (const sib of this._siblings) {
            const sibTexture = sib.texture;
            if (sibTexture && !sibTexture.isEmpty) {
                const sibGrid = sibTexture.textureGrid.clone();
                this._siblingStates.push({
                    roi: sib,
                    originalGrid: sibGrid,
                    originalFlatPixels: flattenSlices(
                        sibTexture.pixels,
                        sibGrid
                    ),
                });
            }
        }

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        if (
            !this._startView ||
            !this._startPatient ||
            !this._originalGrid ||
            !this._originalFlatPixels
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Defer startModify until the first actual drag
        if (!this._textureWrite) {
            this._textureWrite = this._roi.startModify('translate');
            for (const state of this._siblingStates) {
                state.textureWrite = state.roi.startModify('translate');
            }
        }

        // Always use the view where the drag started so the
        // coordinate frame is consistent across the whole gesture.
        const currentPatient =
            this._startView.clientToPatientCoordinates(coordinates);
        const delta = vec3.subtract(
            vec3.create(),
            currentPatient,
            this._startPatient
        );

        const texture = this._roi.texture;
        if (!texture) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const imageGrid = texture.grid;
        const clampedDelta = clampTranslationToImageBounds(
            delta,
            this._originalGrid,
            imageGrid
        );
        const clampedMat = mat4.fromTranslation(mat4.create(), clampedDelta);
        const newGrid = this._originalGrid.transform(clampedMat);

        texture.resetGrid(newGrid);

        // Apply same translation to siblings
        for (const state of this._siblingStates) {
            const sibTexture = state.roi.texture;
            if (sibTexture) {
                const sibGrid = state.originalGrid.transform(clampedMat);
                sibTexture.resetGrid(sibGrid);
            }
        }

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2
    ): EventHandlerResult {
        // Resample the SDF onto a grid snapped to the image voxel
        // lattice. This preserves the contour shape the user saw
        // during the drag while avoiding sub-voxel misalignment
        // that would degrade downstream operations (boolean ops,
        // margins, dose mapping).
        if (this._textureWrite && this._originalFlatPixels) {
            try {
                const texture = this._roi.texture;
                if (texture) {
                    // Use the current (translated) grid as source so the
                    // resample only corrects the sub-voxel snap offset,
                    // not the full translation.
                    const translatedGrid = texture.textureGrid;
                    const snappedGrid = buildSnappedGrid(
                        translatedGrid,
                        texture.grid
                    );
                    this.resampleOntoGrid(
                        texture,
                        this._originalFlatPixels,
                        translatedGrid,
                        snappedGrid
                    );
                }

                // Resample siblings
                for (const state of this._siblingStates) {
                    const sibTexture = state.roi.texture;
                    if (sibTexture && state.textureWrite) {
                        const sibTranslated = sibTexture.textureGrid;
                        const snapped = buildSnappedGrid(
                            sibTranslated,
                            sibTexture.grid
                        );
                        this.resampleOntoGrid(
                            sibTexture,
                            state.originalFlatPixels,
                            sibTranslated,
                            snapped
                        );
                    }
                }
            } finally {
                // Always finalize modify operations to avoid leaving
                // ROIs in a partially-modified state.
                this._textureWrite.finish();
                for (const state of this._siblingStates) {
                    state.textureWrite?.finish();
                }
            }
        }

        this._startView = undefined;
        this._startPatient = undefined;
        this._originalGrid = undefined;
        this._originalFlatPixels = undefined;
        this._textureWrite = undefined;
        this._siblingStates = [];

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    /**
     * Resample original SDF pixels from {@link srcGrid} onto
     * {@link dstGrid} and commit to the texture.
     */
    private resampleOntoGrid(
        texture: {
            resetSdfAndGrid(arrays: Uint8Array[], newGrid: Grid): void;
        },
        srcFlatPixels: Uint8Array,
        srcGrid: Grid,
        dstGrid: Grid
    ): void {
        const result = resampleUint8Grid(srcFlatPixels, srcGrid, dstGrid, {
            backgroundValue: 0,
        });
        const slices = unflattenToSlices(
            result.pixels,
            dstGrid.sizeX,
            dstGrid.sizeY,
            dstGrid.sizeZ
        );
        texture.resetSdfAndGrid(slices, dstGrid);
    }
}
